import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, FormControlLabel, Switch, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import GenericModal from '../../components/GenericModal'
import DefaultModalActions from '../../components/GenericModal/DefaultModalActions'
import { DistributionManager } from '../../APIManager'
import { type GetAllDistributionSeriesResult } from '../../APIManager/DistributionManager'
import { notification } from '../../components/Notifications'
import { type ApiError } from '../../models/Common'
import { grey } from '../../colors'

/**
 * The pair the modal saves. `reopen` is only ever true alongside `dateChanges` — reopening is a
 * date change made against a closed section, so it cannot outlive the setting it depends on.
 */
interface InstructorSurveyDateSettings {
  dateChanges: boolean
  reopen: boolean
}

/** Ties the reopen switch to the line explaining why it is disabled, for a screen reader. */

export interface InstructorSurveyDateChangeModalProps {
  distributionSeriesId: string
  seriesTitle?: string
  /**
   * The value the menu row's chip is showing, used only until the series itself answers.
   *
   * Not trusted as the switch's value: it comes from the scoped `['projects', scope]` list, which
   * can be stale, and resolves to `undefined` → `false` whenever the selected `projectId` is not
   * in the list for the current scope. Saving from that state would write `false` over an enabled
   * setting, so the modal confirms against the series on open — the same thing the grade-posting
   * sibling does, and the reason it does it.
   */
  isEnabled: boolean
  canEdit: boolean
  isOpen: boolean
  handleCloseModal: () => void
}

/**
 * The series-level switch for instructor-driven survey date changes (CR-337).
 *
 * Sits behind the *Allow Instructors to Change Survey Dates* row in the survey project series
 * overflow menu, whose chip shows On or Off — the same shape as Sync Course Sections. Applies to
 * every term in the series; there is no per-term override.
 *
 * Holds two switches. The second, reopening closed surveys, is a power inside the first rather
 * than beside it, so it is disabled and cleared whenever the first is off.
 */
const InstructorSurveyDateChangeModal: React.FC<InstructorSurveyDateChangeModalProps> = ({
  distributionSeriesId,
  seriesTitle,
  isEnabled,
  canEdit,
  isOpen,
  handleCloseModal
}) => {
  const queryClient = useQueryClient()
  const [enabled, setEnabled] = useState(isEnabled)
  // No prop seeds this one: the menu row's chip shows only the date-change setting, so the series
  // read is the sole source and Off is what it starts from.
  const [reopenEnabled, setReopenEnabled] = useState(false)
  // Whether this opening has already taken its starting value. Once it has, the switch belongs to
  // the user: see the seeding effect below.
  const hasSeededRef = useRef(false)
  // Mirrors `hasSeededRef` for render. The ref guards the effect against re-entry; this drives
  // `canSave`, which has to survive a refetch the ref cannot express because it never re-renders.
  const [hasSeeded, setHasSeeded] = useState(false)

  const { data: seriesData, isLoading, isFetching, isError } = useQuery({
    queryKey: ['distributionSeriesProject', distributionSeriesId],
    queryFn: async () => {
      const response = await DistributionManager.getProjectById(distributionSeriesId)
      return response.data?.data ?? response.data
    },
    enabled: isOpen && distributionSeriesId !== ''
  })

  const seriesValue = seriesData?.isInstructorSurveyDateChangeEnabled
  const seriesReopenValue = seriesData?.isInstructorSurveyReopenEnabled
  const loading = isLoading || isFetching

  // Seeded once per opening, so a cancelled edit does not leave the switch on last time's
  // position, and the series' own answer is preferred over the list value the chip was drawn from.
  //
  // Once, and not on every change of either source. Seeding on `[seriesValue, isEnabled]` made a
  // save flip the switch under the user: `onSuccess` invalidates both queries while this modal is
  // still mounted, the `projects` list lands first and changes `isEnabled`, and `seriesValue` is
  // still React Query's previous value — so `seriesValue ?? isEnabled` re-seeded from the stale
  // one and the switch snapped back to where it started, a beat before the modal closed. A
  // background refetch, a window refocus included, threw away an unsaved toggle the same way.
  useEffect(() => {
    if (!isOpen) {
      hasSeededRef.current = false
      setHasSeeded(false)
      return
    }
    if (hasSeededRef.current) return
    // Wait for the read to settle, not merely for a value to exist. `['distributionSeriesProject',
    // id]` is shared with `GradePostingInstructorReleaseModal` and carries the client's default
    // `staleTime: 0`, so opening this modal after that one hands over a cached value with a
    // refetch still in flight — seeding from it and then locking would leave the switch showing
    // what another admin's session saw, and Save would write it back series-wide. The flicker this
    // wait avoids is the same reason it does not seed from the list value first. A read that
    // fails settles too, and falls back to the list value with `canSave` already false.
    if (loading) return
    // A failed read is not a value, and it must not count as seeded: leaving the flag down keeps
    // Save out of reach and lets a later successful read still seed properly, which is the only
    // way back from a GET that 500'd while the modal was open.
    if (isError) {
      setEnabled(Boolean(isEnabled))
      setReopenEnabled(false)
      return
    }
    hasSeededRef.current = true
    setHasSeeded(true)
    const nextEnabled = Boolean(seriesValue ?? isEnabled)
    setEnabled(nextEnabled)
    // Clamped to the parent here, not only in its `onChange`. The column carries no CHECK
    // constraint — the backend keeps the pair unreachable through its own gate rather than
    // disallowing it — so a series row holding reopen on beside date changes off is storable, and
    // seeding it verbatim would show a checked-but-disabled switch beside the line saying it needs
    // the setting above, and write that pair back on Save.
    setReopenEnabled(nextEnabled && Boolean(seriesReopenValue))
  }, [isOpen, seriesValue, seriesReopenValue, isEnabled, loading, isError])

  // Gated on the *seed*, not on the query's current state. A failed read is still not a value —
  // the effect above refuses to seed from one, so `hasSeeded` stays false and Save stays out of
  // reach, which is what stops a GET 500 writing the prop back series-wide.
  //
  // But once a good value has been seeded, this must not move again. `staleTime` is 0 on
  // `['distributionSeriesProject', id]`, so a window refocus refetches: gating on `loading` blanked
  // Save and both switches mid-edit, and gating on `isError` meant a failed background refetch
  // latched an already-loaded, possibly edited setting into unsaveable for as long as the modal
  // stayed open. Neither says anything about the value in hand. Found by Bugbot on #1948.
  const canSave = canEdit && hasSeeded

  const { mutate: saveSetting, isPending: isSaving } = useMutation({
    mutationFn: async ({ dateChanges, reopen }: InstructorSurveyDateSettings) => {
      return await DistributionManager.updateProject(distributionSeriesId, {
        isInstructorSurveyDateChangeEnabled: dateChanges,
        isInstructorSurveyReopenEnabled: reopen
      })
    },
    onSuccess: async (_data, { dateChanges, reopen }) => {
      let message = 'Instructors can no longer change their own survey dates.'
      if (dateChanges) {
        message = reopen
          ? 'Instructors can now change their own survey dates, including reopening closed surveys.'
          : 'Instructors can now change their own survey dates.'
      }
      notification(message, { variant: 'success' })
      // Written into the cache, then closed, then refreshed in the background.
      //
      // Closing first is what keeps a half-updated pair of sources away from the switch. But the
      // menu row's chip reads the `['projects', scope]` list, so closing before the refetch lands
      // left it showing the old On/Off for as long as the round trip took — reopening the menu
      // straight after saving showed the value the save had just replaced. The write succeeded, so
      // the cache is told what it wrote rather than being asked to go and find out; the
      // invalidations below still run, and the server's own answer reconciles behind the modal.
      queryClient.setQueriesData<GetAllDistributionSeriesResult>(
        { queryKey: ['projects'] },
        (previous) => {
          if (!previous?.data) return previous
          return {
            ...previous,
            data: previous.data.map((item) => (
              item?.data?.distributionSeriesId === distributionSeriesId
                ? {
                    ...item,
                    data: {
                      ...item.data,
                      isInstructorSurveyDateChangeEnabled: dateChanges,
                      isInstructorSurveyReopenEnabled: reopen
                    }
                  }
                : item
            ))
          }
        }
      )
      queryClient.setQueryData(
        ['distributionSeriesProject', distributionSeriesId],
        (previous: Record<string, unknown> | undefined) => (
          previous == null
            ? previous
            : {
                ...previous,
                isInstructorSurveyDateChangeEnabled: dateChanges,
                isInstructorSurveyReopenEnabled: reopen
              }
        )
      )
      handleCloseModal()
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['distributionSeriesProject', distributionSeriesId] })
    },
    onError: (error: ApiError) => {
      notification(error?.message ?? 'Failed to save setting. Please try again.', { variant: 'error' })
    },
    meta: { silent: true }
  })

  const handleSave = (): void => {
    if (!canSave) return
    saveSetting({ dateChanges: enabled, reopen: reopenEnabled })
  }

  return (
    <GenericModal
      isOpen={isOpen}
      title="Instructor Survey Date Changes"
      onClose={handleCloseModal}
      maxWidth="sm"
      action={false}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        {seriesTitle && (
          <Typography variant="body2" color={grey[700]}>
            Survey Project: <strong>{seriesTitle}</strong>
          </Typography>
        )}

        <FormControlLabel
          sx={{ alignItems: 'flex-start', ml: 0 }}
          control={
            <Switch
              checked={enabled}
              onChange={(_event, checked) => {
                setEnabled(checked)
                // Reopen follows its parent down rather than waiting to be stripped at save, so
                // the switch the admin is looking at is the setting that gets written.
                if (!checked) setReopenEnabled(false)
              }}
              disabled={!canSave || isSaving}
            />
          }
          label="Allow instructors to change survey dates"
        />

        {/* Two different failures, and they used to share one banner. Before a good seed the read
            is all there is, so a failure means the setting genuinely cannot be changed. After one,
            a failed background refetch costs only freshness — Save and both switches stay live,
            and telling the reader to close the dialog would throw away an edit in progress.
            Found by Bugbot on #1949, as the other half of the `canSave` change. */}
        {isError && !hasSeeded && (
          <Alert severity="error">
            This setting could not be loaded, so it cannot be changed right now. Close the dialog and
            try again.
          </Alert>
        )}

        {isError && hasSeeded && (
          <Alert severity="warning">
            This setting could not be refreshed just now, so it may have changed elsewhere since you
            opened this. Your changes will still save.
          </Alert>
        )}

        <Alert severity="info">
          <Typography variant="body2">
            Instructors, Additional Instructors and Teaching Assistants can change dates on the
            course sections they teach:
          </Typography>
          <Box component="ul" m={0} my={1} pl={3}>
            {/* Not "at any time": once the survey closes, moving the end date is a reopen, which
                the switch below grants separately and may not grant at all. */}
            <Typography component="li" variant="body2">the end date, until the survey closes</Typography>
            <Typography component="li" variant="body2">the start date, while the survey is upcoming</Typography>
          </Box>
          <Typography variant="body2">
            Results release dates follow the new end date. Covers{' '}
            <strong>every term in this survey project</strong>.
          </Typography>
        </Alert>

        {/* Below the alert the first switch explains itself in, because reopening is the heavier
            power of the two and reads as an escalation of what has just been described.

            Hidden rather than disabled while date changes are off. A disabled switch with
            "Requires survey date changes." underneath was asking the admin to read an explanation
            for a control they had not asked for; reopening is only a question once dates can move
            at all, so it appears when it becomes answerable. */}
        {enabled && (
          <FormControlLabel
            sx={{ alignItems: 'flex-start', ml: 0 }}
            control={
              <Switch
                checked={reopenEnabled}
                onChange={(_event, checked) => { setReopenEnabled(checked) }}
                disabled={!canSave || isSaving}
              />
            }
            label="Allow instructors to reopen closed surveys"
          />
        )}

        {reopenEnabled && (
          <Alert severity="warning">
            <Typography variant="body2">
              Reopening a closed survey pushes its results release date out, hides the reports that
              already exist for the section, and regenerates them only after the new end date.
            </Typography>
          </Alert>
        )}

        <DefaultModalActions
          confirmButtonProps={{
            text: 'Save',
            onClick: handleSave,
            loading: isSaving,
            disabled: !canSave
          }}
          destructiveButtonProps={{
            onClick: handleCloseModal,
            disabled: isSaving
          }}
        />
      </Box>
    </GenericModal>
  )
}

export default InstructorSurveyDateChangeModal
